<!-- Loading Cart — shown until the first fetch finishes, so the empty screen never flashes -->
<div *ngIf="cartLoading" class="min-h-[70vh] flex items-center justify-center font-serif">
  <div class="text-center space-y-3">
    <div class="w-10 h-10 border-[3px] border-gray-200 border-t-[#600b0b] rounded-full animate-spin mx-auto"></div>
    <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">Loading your cart&#8230;</p>
  </div>
</div>

<!-- Empty Cart -->
<div *ngIf="!cartLoading && items.length === 0" class="min-h-[70vh] flex flex-col items-center justify-center text-center px-6 font-serif">
  <div class="relative mb-8 gentle-float">
    <div class="w-28 h-28 rounded-full bg-gradient-to-br from-rose-50 to-rose-100 flex items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-rose-300"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
    </div>
  </div>
  <h2 class="text-3xl font-serif font-medium text-gray-800 mb-3">Your Cart is Empty</h2>
  <p class="text-gray-400 max-w-sm mb-8 text-sm">Discover wallpapers that transform your space into a masterpiece.</p>
  <button (click)="navigateToListing()" class="bg-rose-900 text-white px-10 py-4 rounded-xl font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-rose-800 transition-all flex items-center gap-2 shadow-lg">
    Explore Collections
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
  </button>
  <div class="mt-16 w-full max-w-4xl">
    <h3 class="text-lg font-serif font-medium text-gray-700 mb-6">Popular Picks For You</h3>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div *ngFor="let wp of recommendations" (click)="navigateToPDP(wp.id)" class="group cursor-pointer">
        <div class="aspect-[3/2] rounded-xl overflow-hidden shadow-sm group-hover:shadow-lg transition-all">
          <img [src]="wp.image" [alt]="wp.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2s]" />
        </div>
        <p class="mt-2 text-sm font-serif text-gray-700 truncate group-hover:text-rose-900 transition-colors">{{ wp.name }}</p>
        <p class="text-sm font-medium text-rose-900">₹{{ wp.price }}</p>
      </div>
    </div>
  </div>
</div>

<!-- Cart with items -->
<div *ngIf="items.length > 0" class="relative max-w-7xl mx-auto px-6 py-12 font-serif">
  <!--
    Blocking overlay while a quantity update + its reload are in flight. It covers
    only the cart region (not the global nav), disables pointer events on the cart,
    and stays up until AppStateService clears `cartBusy$` — which happens only AFTER
    the freshest cart/Order Summary state is painted, never merely when the HTTP
    response returns. Belt-and-suspenders with the disabled +/-/Remove controls.
  -->
  <div *ngIf="cartBusy" class="absolute inset-0 z-30 flex items-center justify-center bg-white/60 backdrop-blur-[1px] cursor-wait" aria-busy="true" aria-live="polite">
    <div class="flex flex-col items-center gap-3">
      <div class="w-10 h-10 rounded-full border-[3px] border-gray-200 border-t-[#600b0b] animate-spin"></div>
      <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">Updating cart…</p>
    </div>
  </div>
  <div class="flex flex-col items-center mb-8 relative">
    <h1 class="text-3xl font-serif font-medium text-gray-800 mb-2 tracking-wider">Your Cart</h1>
    <p class="text-xs text-gray-400">{{ totalItems }} {{ totalItems === 1 ? 'item' : 'items' }} in your cart</p>
  </div>

  <!-- Free Shipping Progress -->
  <div class="max-w-2xl mx-auto mb-8 bg-[#faf8f5] rounded-xl p-4 border border-gray-100">
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-[#600b0b]"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
        {{ amountToFreeShipping > 0 ? 'Add ₹' + amountToFreeShipping.toFixed(0) + ' more for free shipping' : 'You qualify for free shipping!' }}
      </div>
      <span *ngIf="amountToFreeShipping <= 0" class="text-[10px] font-bold text-green-600 uppercase tracking-wider">Unlocked</span>
    </div>
    <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
      <div class="h-full rounded-full transition-all duration-700 ease-out fill-bar"
        [style.width.%]="freeShippingProgress"
        [style.background]="freeShippingProgress >= 100 ? '#16a34a' : 'linear-gradient(90deg, #600b0b, #c49a6c)'">
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-10">
    <!-- Items -->
    <div class="lg:col-span-2 space-y-4">
      <div *ngFor="let item of items" class="flex flex-col sm:flex-row gap-6 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group">
        <div (click)="navigateToPDP(item.id)" class="w-full sm:w-28 aspect-square rounded-xl overflow-hidden shrink-0 cursor-pointer">
          <img [src]="item.image" [alt]="item.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div class="flex-grow flex flex-col justify-between">
          <div class="flex justify-between items-start">
            <div>
              <h3 (click)="navigateToPDP(item.id)" class="text-lg font-serif font-medium text-gray-800 mb-0.5 cursor-pointer hover:text-rose-900 transition-colors">{{ item.name }}</h3>
              <p class="text-[9px] uppercase tracking-widest text-gray-400 font-bold">{{ item.collection }} &bull; SKU: {{ item.sku }}</p>
            </div>
            <button (click)="removeItem(item)" [disabled]="cartBusy" class="text-gray-300 hover:text-rose-800 transition-colors p-1 disabled:opacity-40 disabled:cursor-not-allowed">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path></svg>
            </button>
          </div>
          <div class="flex items-center justify-between mt-4">
            <!--
              Quantity is editable only when `quantityEditable` is on — see the
              comment on that field. Installation is not re-resolved for a scaled
              area on old-shape cart rows, so offering these buttons today would
              give installation away on the extra units.
            -->
            <div class="flex items-center border border-gray-100 rounded-full p-0.5 bg-gray-50/50">
              <button *ngIf="quantityEditable" (click)="updateQuantity(item, -1)"
                [disabled]="item.quantity <= 1 || cartBusy"
                class="p-1.5 hover:bg-white rounded-full transition-all text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
              <span class="px-2 text-center font-bold text-sm text-gray-800"><span *ngIf="!quantityEditable"
                  class="text-[9px] uppercase tracking-widest text-gray-400">Qty&nbsp;</span>{{ item.quantity }}</span>
              <button *ngIf="quantityEditable" (click)="updateQuantity(item, 1)" [disabled]="cartBusy"
                class="p-1.5 hover:bg-white rounded-full transition-all text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
            </div>
            <div class="text-right">
              <p class="text-xl font-bold text-gray-900">₹{{ (item.price * item.quantity).toFixed(0) }}</p>
              <p class="text-[10px] text-gray-400">₹{{ item.price }} each</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Summary -->
    <div class="space-y-4">
      <!-- Promo Code -->
      <div class="bg-[#faf8f5] rounded-2xl p-5 border border-gray-100">
        <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-600 mb-3">Promo Code</p>
        <div class="flex gap-2">
          <input [(ngModel)]="promoCode" (keydown.enter)="applyPromo()" placeholder="Enter code" class="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#600b0b] uppercase font-mono tracking-wider" />
          <button (click)="applyPromo()" class="bg-[#600b0b] text-white px-4 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#8c1c1c] transition-all">Apply</button>
        </div>
        <p *ngIf="promoError" class="text-xs text-rose-600 mt-2">{{ promoError }}</p>
        <p *ngIf="promoApplied" class="text-xs text-green-600 mt-2 font-bold">{{ promoDiscount }}% discount applied!</p>
      </div>

      <!-- Summary -->
      <div class="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm space-y-4">
        <p class="text-sm font-bold uppercase tracking-[0.2em] text-gray-700">Order Summary</p>
        <div class="space-y-2">
          <div class="flex justify-between text-sm text-gray-600">
            <span>Subtotal ({{ totalItems }} items)</span>
            <span class="font-bold">₹{{ subtotal.toFixed(0) }}</span>
          </div>
          <div *ngIf="promoApplied" class="flex justify-between text-sm text-green-600">
            <span>Discount ({{ promoDiscount }}%)</span>
            <span class="font-bold">-₹{{ discountAmount.toFixed(0) }}</span>
          </div>
          <div class="flex justify-between text-sm text-gray-600">
            <span>Shipping</span>
            <span class="font-bold text-green-600">Free</span>
          </div>
          <div class="pt-3 border-t border-gray-100 flex justify-between">
            <span class="font-bold text-gray-900">Total</span>
            <span class="text-xl font-bold text-gray-900">₹{{ total.toFixed(0) }}</span>
          </div>
        </div>
        <div class="text-[10px] text-gray-400 flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect></svg>
          Estimated delivery: {{ getDeliveryStart() }} – {{ getDeliveryEnd() }}
        </div>
        <button (click)="router.navigate(['/checkout'])" class="w-full bg-[#600b0b] text-white py-4 rounded-xl font-bold uppercase tracking-[0.15em] text-[10px] hover:bg-[#8c1c1c] transition-all shadow-lg btn-pulse">
          Proceed to Checkout
        </button>
        <button (click)="navigateToListing()" class="w-full border border-gray-200 text-gray-600 py-3 rounded-xl font-bold uppercase tracking-[0.15em] text-[10px] hover:border-[#600b0b] hover:text-[#600b0b] transition-all">
          Continue Shopping
        </button>
      </div>
    </div>
  </div>

  <!-- Recommendations -->
  <div *ngIf="recommendations.length > 0" class="mt-16">
    <h3 class="text-xl font-serif font-medium text-gray-700 mb-6">Complete Your Look</h3>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div *ngFor="let wp of recommendations" (click)="navigateToPDP(wp.id)" class="group cursor-pointer">
        <div class="aspect-[3/2] rounded-xl overflow-hidden shadow-sm group-hover:shadow-lg transition-all">
          <img [src]="wp.image" [alt]="wp.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2s]" />
        </div>
        <p class="mt-2 text-sm font-serif text-gray-700 truncate group-hover:text-rose-900 transition-colors">{{ wp.name }}</p>
        <div class="flex items-center justify-between">
          <p class="text-sm font-medium text-rose-900">₹{{ wp.price }}</p>
          <button (click)="addRecommendation(wp); $event.stopPropagation()" class="text-[9px] font-bold uppercase tracking-wider text-[#600b0b] hover:underline">Add</button>
        </div>
      </div>
    </div>
  </div>
</div>
